export enum PayPeriod {
  weekly = 'weekly',
  biWeekly = 'bi_weekly',
  semiMonthly = 'semi_monthly',
  monthly = 'monthly',
}

export enum PayrollStatus {
  draft = 'draft',
  processing = 'processing',
  completed = 'completed',
  cancelled = 'cancelled',
}

export enum BenefitType {
  health = 'health',
  dental = 'dental',
  vision = 'vision',
  life = 'life',
  disability = 'disability',
  retirement401k = 'retirement_401k',
  hsa = 'hsa',
  fsa = 'fsa',
  other = 'other',
}

export enum DeductionType {
  preTax = 'pre_tax',
  postTax = 'post_tax',
  garnishment = 'garnishment',
  other = 'other',
}

export enum DeductionCategory {
  // Pre-tax deductions
  retirement401k = 'retirement_401k',
  retirement403b = 'retirement_403b',
  retirement457 = 'retirement_457',
  hsa = 'hsa',
  fsa = 'fsa',
  dependentCareFSA = 'dependent_care_fsa',
  commuterBenefits = 'commuter_benefits',
  parkingBenefits = 'parking_benefits',
  lifeInsurance = 'life_insurance',
  disabilityInsurance = 'disability_insurance',
  healthInsurance = 'health_insurance',
  dentalInsurance = 'dental_insurance',
  visionInsurance = 'vision_insurance',
  
  // Post-tax deductions
  roth401k = 'roth_401k',
  roth403b = 'roth_403b',
  unionDues = 'union_dues',
  charitableContributions = 'charitable_contributions',
  stockPurchase = 'stock_purchase',
  loanRepayment = 'loan_repayment',
  uniformDeduction = 'uniform_deduction',
  toolDeduction = 'tool_deduction',
  parkingFees = 'parking_fees',
  otherVoluntary = 'other_voluntary',
  
  // Garnishments and legal
  wageGarnishment = 'wage_garnishment',
  childSupport = 'child_support',
  taxLevy = 'tax_levy',
  studentLoanGarnishment = 'student_loan_garnishment',
  bankruptcy = 'bankruptcy',
  
  // Other
  other = 'other',
}

export enum DeductionCalculationMethod {
  fixedAmount = 'fixed_amount',
  percentage = 'percentage',
  tiered = 'tiered',
  cappedPercentage = 'capped_percentage',
  minimumMaximum = 'minimum_maximum',
  custom = 'custom',
}

export enum DeductionFrequency {
  perPaycheck = 'per_paycheck',
  weekly = 'weekly',
  biWeekly = 'bi_weekly',
  semiMonthly = 'semi_monthly',
  monthly = 'monthly',
  quarterly = 'quarterly',
  annually = 'annually',
  oneTime = 'one_time',
  custom = 'custom',
}

export enum DeductionStatus {
  active = 'active',
  inactive = 'inactive',
  pending = 'pending',
  suspended = 'suspended',
  terminated = 'terminated',
  expired = 'expired',
}

export enum GarnishmentType {
  childSupport = 'child_support',
  spousalSupport = 'spousal_support',
  taxLevy = 'tax_levy',
  studentLoan = 'student_loan',
  creditorGarnishment = 'creditor_garnishment',
  bankruptcy = 'bankruptcy',
  other = 'other',
}

export interface DeductionLimit {
  minimum?: number;
  maximum?: number;
  annualMaximum?: number;
  lifetimeMaximum?: number;
  perPaycheckMaximum?: number;
}

export interface DeductionTier {
  from: number;
  to?: number; // undefined means no upper limit
  amount?: number;
  percentage?: number;
}

export interface GarnishmentDetails {
  type: GarnishmentType;
  caseNumber?: string;
  courtOrderNumber?: string;
  courtOrderDate?: Date;
  courtOrderExpiryDate?: Date;
  priority: number; // Lower number = higher priority
  exemptAmount?: number; // Amount protected from garnishment
  maximumPercentage?: number; // Maximum % of disposable income
  attorneyFees?: number;
  processingFee?: number;
  recipientName?: string;
  recipientAddress?: string;
  recipientAccountNumber?: string;
  recipientRoutingNumber?: string;
  isActive: boolean;
}

export interface Deduction {
  id: string;
  employeeId: string;
  employeeName: string;
  employerId?: string;
  
  // Basic Information
  type: DeductionType;
  category: DeductionCategory;
  name: string;
  description?: string;
  
  // Calculation
  calculationMethod: DeductionCalculationMethod;
  amount?: number; // Fixed amount
  percentage?: number; // Percentage of gross or net
  percentageBase?: 'gross' | 'net' | 'taxable'; // What the percentage is calculated from
  tiers?: DeductionTier[]; // For tiered calculations
  customCalculationFormula?: string; // For custom calculations
  
  // Limits and Caps
  limits?: DeductionLimit;
  
  // Frequency and Timing
  frequency: DeductionFrequency;
  startDate: Date;
  endDate?: Date;
  effectiveDate?: Date;
  terminationDate?: Date;
  
  // Status
  status: DeductionStatus;
  requiresApproval: boolean;
  approvedBy?: string;
  approvedAt?: Date;
  
  // Garnishment Details (if applicable)
  garnishmentDetails?: GarnishmentDetails;
  
  // Priority and Ordering
  priority: number; // Lower number = applied first (for pre-tax deductions)
  postTaxPriority?: number; // Priority for post-tax deductions
  
  // Compliance and Legal
  isMandatory: boolean; // Cannot be cancelled by employee
  requiresEmployeeConsent: boolean;
  employeeConsentDate?: Date;
  employeeConsentSignature?: string;
  legalDocumentUrl?: string;
  complianceNotes?: string;
  
  // Tracking
  totalDeductedYTD: number;
  totalDeductedLifetime: number;
  lastDeductionDate?: Date;
  lastDeductionAmount?: number;
  deductionCount: number;
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  lastUpdated: Date;
  lastUpdatedBy: string;
  version: number; // For audit trail
  
  // Additional Fields
  vendorId?: string; // If deduction goes to a vendor
  accountNumber?: string; // Employee's account with vendor
  notes?: string;
  tags?: string[];
  metadata?: Record<string, any>;
}

export interface DeductionTemplate {
  id: string;
  name: string;
  description?: string;
  category: DeductionCategory;
  type: DeductionType;
  calculationMethod: DeductionCalculationMethod;
  defaultAmount?: number;
  defaultPercentage?: number;
  defaultLimits?: DeductionLimit;
  isStandard: boolean; // Standard templates vs custom
  applicableToRoles?: string[];
  metadata?: Record<string, any>;
  createdAt: Date;
  lastUpdated: Date;
}

export interface DeductionCalculationResult {
  deductionId: string;
  deductionName: string;
  calculatedAmount: number;
  baseAmount: number; // The amount the calculation was based on
  method: DeductionCalculationMethod;
  applied: boolean; // Whether deduction was actually applied
  reason?: string; // If not applied, why
  limitsApplied?: {
    minimum?: boolean;
    maximum?: boolean;
    annualMaximum?: boolean;
  };
  metadata?: Record<string, any>;
}

export interface PayrollRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  payPeriod: PayPeriod;
  periodStart: Date;
  periodEnd: Date;
  payDate: Date;
  status: PayrollStatus;
  
  // Earnings
  grossPay: number;
  regularHours?: number;
  overtimeHours?: number;
  hourlyRate?: number;
  salaryAmount?: number;
  bonuses?: number;
  commissions?: number;
  otherEarnings?: number;
  
  // Deductions
  federalTax: number;
  stateTax: number;
  localTax: number;
  socialSecurity: number;
  medicare: number;
  otherDeductions: number;
  totalDeductions: number;
  
  // Enhanced Deduction Tracking
  preTaxDeductions?: DeductionCalculationResult[];
  postTaxDeductions?: DeductionCalculationResult[];
  garnishments?: DeductionCalculationResult[];
  deductionBreakdown?: {
    preTaxTotal: number;
    postTaxTotal: number;
    garnishmentTotal: number;
    taxTotal: number;
    otherTotal: number;
  };
  
  // Net Pay
  netPay: number;
  
  // Benefits
  benefitsCost: number;
  
  // Metadata
  notes?: string;
  processedBy?: string;
  processedAt?: Date;
  createdAt: Date;
  lastUpdated: Date;
}

export interface Benefit {
  id: string;
  employeeId: string;
  employeeName: string;
  type: BenefitType;
  provider?: string;
  planName?: string;
  coverageLevel?: 'individual' | 'family';
  employeeContribution: number;
  employerContribution: number;
  totalCost: number;
  effectiveDate: Date;
  endDate?: Date;
  enrollmentDate: Date;
  status: 'active' | 'pending' | 'terminated';
  dependents?: string[];
  createdAt: Date;
  lastUpdated: Date;
}


export interface TaxForm {
  id: string;
  employeeId: string;
  employeeName: string;
  formType: 'w2' | '1099' | 'w4';
  taxYear: number;
  fileUrl: string;
  generatedAt: Date;
  generatedBy: string;
  metadata?: Record<string, unknown>;
}

export interface PayrollSummary {
  periodStart: Date;
  periodEnd: Date;
  payDate: Date;
  totalEmployees: number;
  totalGrossPay: number;
  totalDeductions: number;
  totalNetPay: number;
  totalBenefitsCost: number;
  status: PayrollStatus;
  records: PayrollRecord[];
}

